/* eslint-disable max-lines */
import H7 from '@base/H7/H7';
import Image from '@base/Image';
import { LANGUAGE_MAP } from '@cont/Content/FreeLesson/utils';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import { commasIndianFormat, currencyCode, pluralize } from '@utils/textHelper';
import { useEffect, useState, useMemo } from 'react';
import {
  getDateMonthString,
  getDay,
  getYear,
  getMonthString,
  getOnlyIntegerDateDifference,
  timeTillFromSeconds
} from '@utils/dates';
import { useRouter } from 'next/router';
import {
  getIsSubscribedToIconic,
  getIsSubscribedToLite,
  getIsSubscribedToPlus
} from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import segmentStorage from '@events/segmentStorage';
import { BATCH_CONTENT_TYPE_MAP, getIsStoreSyllabus } from '@cont/Batch/utils';
import { isTestSeriesFeed } from '@utils/feedTypeHelper';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { segment } from '@actions/analytics';
import { SEARCH_RESULT_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { emptyObject } from '@constants/empty';
import { storeSubscriptionTypes } from './constants';

const CardWrapper = styled.div`
  flex: none;
  cursor: pointer;
  height: 100%;
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: ${Spacings.SPACING_4B};
  }
`;

const BatchThumbnail = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  min-width: 272px;
  min-height: 152px;
  position: relative;
  & > span {
    border-radius: ${Spacings.SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    max-height: 172px;
    min-height: 172px;
    min-width: 130px;
  }
`;

const BatchTypeTag = styled.div`
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  background: var(--color-base-1);
  border: 1px solid var(--color-divider);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  color: var(--color-text-primary);
  position: absolute;
  top: ${Spacings.SPACING_2B};
  left: ${Spacings.SPACING_2B};
  text-transform: uppercase;
  z-index: 1;
  border-radius: ${Spacings.SPACING_1B};
`;

const BatchDetails = styled.div<{ $includeCta: boolean }>`
  width: 272px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${Spacings.SPACING_3B};
  gap: ${Spacings.SPACING_6};
  position: relative;
  height: ${({ $includeCta }) => ($includeCta ? '200px' : 'auto')};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    justify-content: center;
  }
`;

const LanguageTopicGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const Language = styled(Typography)`
  display: flex;
  justify-content: center;
  background: var(--color-i-white);
  border: 1px solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_1B};
  font-weight: ${Fonts.HEAVY};
  height: 18px;
  width: 26px;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const TopicGroup = styled(H7)<{ $isBatchorNotes: boolean }>`
  font-weight: ${Fonts.HEAVY};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ $isBatchorNotes }) =>
    $isBatchorNotes ? 'var(--color-i-blue)' : 'var(--color-i-blue-25)'};
  ${mediaQuery.uptoMobile} {
    display: -webkit-box;
    font-size: ${Fonts.EXTRA_SMALL};
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
  }
`;

const ClassTitle = styled(Typography)`
  color: var(--color-i-black);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
`;

const ClassDays = styled(Typography)`
  color: var(--color-i-gray-13);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
  }
`;

const LiveDetails = styled(Typography)`
  color: var(--color-i-gray-13);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  padding-right: ${Spacings.SPACING_1};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    width: 100%;
  }
`;

const DiscountWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const DiscountLogo = styled.div`
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
  position: relative;
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_3B};
    height: ${Spacings.SPACING_3B};
  }
`;

const DiscountText = styled(H7)`
  font-weight: ${Fonts.HEAVY};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--color-i-yellow-5);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const CostWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const Cost = styled(Typography)`
  letter-spacing: -0.005em;
  color: var(--color-i-black);
`;

const DiscountCost = styled(Typography)`
  text-decoration-line: line-through;
  color: var(--color-i-gray-13);
`;

const ViewDetailsBtn = styled(AquillaButton)`
  margin-top: ${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_12B};
  bottom: ${Spacings.SPACING_0B};
  position: absolute;

  ${mediaQuery.uptoMobile} {
    height: ${Spacings.SPACING_8B};
    font-size: ${Fonts.P2};
    position: relative;
  }
`;

const StoreCard = ({ data, includeCta = false, source = '' }) => {
  const router = useRouter();
  const [time, setTime] = useState('0');
  const isMobile = useIsMobile();
  const selectedGoalUID = useSelectedGoalUID();
  const { goalUID } = router.query;
  const subscribedGoals = [];
  const isPlusSubscribed = useMyInfo(
    getIsSubscribedToPlus(goalUID || selectedGoalUID)
  );
  const isIconicSubscribed = useMyInfo(
    getIsSubscribedToIconic(goalUID || selectedGoalUID)
  );
  const isLiteSubscribed = useMyInfo(
    getIsSubscribedToLite(goalUID || selectedGoalUID)
  );

  const goalSegmentData = useGoalEventProps({
    goalUID: goalUID || selectedGoalUID
  });

  useEffect(() => {
    const id = setInterval(getLiveTimeDifference, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  if (isPlusSubscribed || isIconicSubscribed || isLiteSubscribed) {
    if (isPlusSubscribed) {
      subscribedGoals.push('Plus');
    }
    if (isIconicSubscribed) {
      subscribedGoals.push('Iconic');
    }
    if (isLiteSubscribed) {
      subscribedGoals.push('Lite');
    }
  } else {
    subscribedGoals.push('NA');
  }

  const {
    productDetails: {
      title,
      syllabusTag,
      noOfClasses,
      noOfLiveClasses,
      noOfRecordedClasses,
      noOfTests,
      isSellable,
      startDate,
      thumbnail,
      contentType,
      uid,
      languages,
      thumbnailV1,
      additionalFeatures,
      subEntities = null
    },
    expiredAt,
    price,
    maxRetailPrice,
    priceEffectiveTill,
    currency,
    contentTypeId,
    subscription,
    accessEndDate,
    duration,
    displayDuration
  } = data || {};

  const isStoreSyllabus = getIsStoreSyllabus({ additionalFeatures });
  let subEntitiesSubTitle = [];
  if (subEntities) {
    subEntitiesSubTitle = subEntities
      .map((subEntity) => {
        return subEntity.label;
      })
      .sort();
  }
  const lastEntityVal = subEntitiesSubTitle.pop();
  const notesSubTitle = `${subEntitiesSubTitle
    .toString()
    .replace(',', ', ')} & ${lastEntityVal}`;

  const {
    duration: browseDuration,
    displayDuration: browseDisplayDuration,
    accessEndDate: browseAccessEndDate
  } = subscription ?? emptyObject;

  const finalDuration = duration || browseDuration;

  const finalDisplayDuration = displayDuration || browseDisplayDuration;

  const isRecorded = contentType === BATCH_CONTENT_TYPE_MAP.RECORDED;
  const isLiveRecordedBatch =
    contentType === BATCH_CONTENT_TYPE_MAP.LIVE_RECORDED;
  const isOfflineBatch = contentType === BATCH_CONTENT_TYPE_MAP.OFFLINE;
  const isBatch =
    contentTypeId === storeSubscriptionTypes.BATCH ||
    subscription?.contentTypeId === storeSubscriptionTypes.BATCH;

  const isNotes =
    contentTypeId === storeSubscriptionTypes.NOTES ||
    subscription?.contentTypeId === storeSubscriptionTypes.NOTES;

  let img = 'store/checkout/batch-web.svg';
  if (isMobile) {
    if (thumbnail) {
      img = thumbnail;
    } else {
      img = 'store/checkout/batch-mweb.svg';
      if (!isBatch) {
        img = 'store/checkout/test-series-mweb.svg';
      }
    }
  } else if (thumbnailV1) {
    img = thumbnailV1;
  } else if (!isBatch) {
    img = 'store/checkout/test-series-web.svg';
  }

  const oneDayInMs = 24 * 60 * 60 * 1000;

  const startsIn = useMemo(() => {
    const oneDayInSec = oneDayInMs / 1000; // one day in second
    if (!startDate) return null;
    const timeDiff =
      (new Date(startDate).getTime() - new Date().getTime()) / 1000;

    if (timeDiff > 0) {
      if (timeDiff < oneDayInSec) {
        if (isRecorded || isLiveRecordedBatch) {
          return null;
        }
        return 'Starts today';
      }
      return `Starts in ${timeTillFromSeconds(timeDiff)}`;
    }
    if (includeCta || isRecorded || isLiveRecordedBatch) return null; // check for store listing page
    return `Started on ${getDay(startDate, true)} ${getMonthString(startDate)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  if (!data) return <></>;

  const calculateDiscountPercentage = () => {
    const percentage = ((maxRetailPrice - price) / maxRetailPrice) * 100;
    return Math.floor(percentage);
  };

  const validTillDate = priceEffectiveTill
    ? new Date(priceEffectiveTill)
    : new Date(browseAccessEndDate);

  const getLiveTimeDifference = () => {
    const todayInMs = new Date().getTime();
    const targetDateInMs = new Date(priceEffectiveTill).getTime();
    const dayDifference = getOnlyIntegerDateDifference(
      todayInMs,
      targetDateInMs
    );
    const dayDifferenceinMs = targetDateInMs - todayInMs;
    if (dayDifference > 0) {
      if (dayDifferenceinMs > 7 * oneDayInMs) {
        setTime(
          `till ${getDay(priceEffectiveTill)} ${getMonthString(
            priceEffectiveTill
          )}`
        );
      } else if (dayDifference === 1) {
        setTime(`for ${dayDifference.toString()} DAY`);
      } else {
        setTime(`for ${dayDifference.toString()} DAYS`);
      }
    } else if (dayDifferenceinMs > 0) {
      const diffInSec = Math.floor(dayDifferenceinMs / 1000);
      const diffInHourMinSec = new Date(diffInSec * 1000)
        .toISOString()
        .substring(11, 19);
      setTime(`for ${diffInHourMinSec}`);
    } else {
      setTime('0');
    }
  };

  const discountPercent = maxRetailPrice ? calculateDiscountPercentage() : 0;

  const storeSyllabusSubText = 'Topic wise classes and tests';

  let liveClasses = '';
  let recordedBatch = '';
  let liveTests = '';
  if (isBatch) {
    if (isRecorded) {
      recordedBatch = noOfRecordedClasses !== 0 ? 'Recorded batch' : '';
      if (noOfRecordedClasses !== 0) {
        liveClasses = pluralize(noOfRecordedClasses, 'recorded class', 'es');
      }
      if (noOfTests !== 0) {
        liveTests = pluralize(noOfTests, 'mock test');
      }
    } else if (isLiveRecordedBatch) {
      liveClasses = pluralize(noOfLiveClasses, 'live class', 'es');
      if (noOfRecordedClasses) {
        liveClasses += `${liveClasses ? ' • ' : ''}${pluralize(
          noOfRecordedClasses,
          'recorded class',
          'es'
        )}`;
      }
      if (noOfTests !== 0) {
        liveTests = pluralize(noOfTests, 'mock test');
      }
    } else {
      if (noOfClasses !== 0 && noOfLiveClasses !== 0) {
        liveClasses =
          noOfClasses === 1 || noOfLiveClasses === 1
            ? `${noOfClasses || noOfLiveClasses} live class `
            : `${noOfClasses || noOfLiveClasses} live classes `;
      }
      if (noOfTests !== 0) {
        liveTests = pluralize(noOfTests, 'mock test');
      }
    }
  } else if (noOfTests !== 0) {
    liveTests = pluralize(noOfTests, 'mock test');
  }

  const storeCardClicked = () => {
    if (!includeCta) {
      segmentStorage.setItem('last_primary_source', 'Store');
    } else {
      segmentStorage.setItem('lps', 'Goal Home');
      segmentStorage.setItem('lpss', 'My Purchases');
    }

    if (source === 'search') {
      segmentStorage.setItem('lps', '');
      segmentStorage.setItem('lpss', 'Search');
      segment.track(SEARCH_RESULT_CLICKED, {
        ...goalSegmentData,
        block_name: 'Unacademy Store',
        object_type: isBatch ? ' batch' : 'test series'
      });
    }

    if (isBatch) {
      router.push(
        '/store/batch/[slug]/[subscriptionUID]',
        `/store/batch/-/${uid}`
      );
    } else if (isNotes) {
      segmentStorage.setItem('lps', 'store');
      router.push('/store/notes/[slug]/[noteUID]', `/store/notes/-/${uid}`);
    } else {
      router.push(
        '/store/test-series/[slug]/[courseUID]',
        `/store/test-series/-/${uid}`
      );
    }
  };
  const showLiveClasses = includeCta
    ? (!recordedBatch || !includeCta) && !startsIn
    : !recordedBatch || !includeCta;

  const liveDetailsText = () => {
    if (isStoreSyllabus) {
      return storeSyllabusSubText;
    }
    if (isNotes) {
      return notesSubTitle;
    }
    return `${liveClasses}
    ${liveTests !== '' && liveClasses !== '' ? ' • ' : ''}
    ${liveTests}`;
  };

  const isDurationBased = includeCta
    ? !browseAccessEndDate && !!finalDuration
    : !accessEndDate && !!finalDuration;

  const getRecordedHybridDuration = () => {
    return includeCta
      ? `Valid till ${
          expiredAt && getDateMonthString(expiredAt, false, false)
        } ${expiredAt && getYear(expiredAt)}`
      : `Valid for ${finalDisplayDuration}`;
  };

  return (
    <CardWrapper onClick={storeCardClicked} className="store-card-wrapper">
      <BatchThumbnail>
        {contentType && isBatch && (
          <BatchTypeTag>
            {isOfflineBatch ? BATCH_CONTENT_TYPE_MAP.CENTER : contentType}
          </BatchTypeTag>
        )}
        <Image src={img} layout="fill" alt="Batch Thumbnail" />
      </BatchThumbnail>
      <BatchDetails $includeCta={includeCta}>
        <LanguageTopicGroup>
          {(isBatch || isNotes) && (
            <Language variant="p3">
              {LANGUAGE_MAP[languages[0]?.code]?.languageText.toUpperCase() ||
                'EN'}
            </Language>
          )}
          <TopicGroup $isBatchorNotes={isBatch || isNotes}>
            {syllabusTag}
          </TopicGroup>
        </LanguageTopicGroup>
        <ClassTitle variant="h5">{title}</ClassTitle>
        {startsIn && !isNotes && <ClassDays variant="p2">{startsIn}</ClassDays>}
        {showLiveClasses && (
          <LiveDetails variant="p2">{liveDetailsText()}</LiveDetails>
        )}
        {isDurationBased && (
          <ClassDays variant="p2">{getRecordedHybridDuration()}</ClassDays>
        )}
        {!includeCta && time !== '0' && discountPercent !== 0 && (
          <DiscountWrapper>
            <DiscountLogo>
              <Image src="discountLogo.svg" layout="fill" alt="Discount Logo" />
            </DiscountLogo>
            <DiscountText>
              {discountPercent}% OFF {time}
            </DiscountText>
          </DiscountWrapper>
        )}
        {!includeCta && (
          <CostWrapper>
            <Cost variant="h5">{`${
              currencyCode[currency || 'INR']
            }${commasIndianFormat(price, true)}`}</Cost>
            {maxRetailPrice > price && (
              <DiscountCost variant="p3">
                {`${currencyCode[currency || 'INR']}${commasIndianFormat(
                  maxRetailPrice,
                  true
                )}`}
              </DiscountCost>
            )}
          </CostWrapper>
        )}
        {includeCta && (
          <ViewDetailsBtn
            label="View details"
            onClick={() => {}}
            fullWidth
            variant="secondary"
          />
        )}
      </BatchDetails>
    </CardWrapper>
  );
};
export default StoreCard;
